import React from "react"
import { CheckCircle2, Circle, Clock, AlarmClock, Pencil, Trash2, BellOff, BellRing, User, RotateCcw } from "lucide-react"
import { cn } from "src/utils/cn"
import { Button } from "src/components/ui/button"
import { Can } from "src/components/shared/Can"

const PRIORITY_STYLES = {
  low:    { dot: "bg-slate-400",  label: "Low",    text: "text-slate-500" },
  medium: { dot: "bg-blue-500",   label: "Med",    text: "text-blue-600" },
  high:   { dot: "bg-amber-500",  label: "High",   text: "text-amber-600" },
  urgent: { dot: "bg-red-500",    label: "Urgent", text: "text-red-600" },
}

function formatDue(due_at) {
  if (!due_at) return null
  const d = new Date(due_at)
  const now = new Date()
  const isToday = d.toDateString() === now.toDateString()
  const isTomorrow = d.toDateString() === new Date(now.getTime() + 86400000).toDateString()

  const time = d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
  if (isToday) return `Today ${time}`
  if (isTomorrow) return `Tomorrow ${time}`
  return d.toLocaleDateString([], { month: "short", day: "numeric" }) + " " + time
}

export function TaskCard({ task, onComplete, onReopen, onEdit, onDelete, onSnooze, onUnsnooze, showContext = false }) {
  const p = PRIORITY_STYLES[task.priority] || PRIORITY_STYLES.medium
  const isOverdue = task.overdue && task.status === "open"
  const isDone = task.status === "completed"

  return (
    <div className={cn(
      "relative flex gap-3 rounded-xl border px-4 py-3 transition-all",
      isDone ? "border-slate-100 bg-slate-50/60" : "border-slate-200 bg-white hover:border-slate-300 hover:shadow-sm",
      isOverdue && !isDone && "border-red-200 bg-red-50/40"
    )}>
      {/* Complete / reopen toggle — complete needs tasks/complete, reopen needs tasks/update. */}
      <Can any={[["tasks", "complete"], ["tasks", "update"]]}>
        <Button
          variant="ghost"
          size="icon"
          onClick={() => isDone ? onReopen?.(task.id) : onComplete?.(task.id)}
          className={cn(
            "mt-0.5 h-auto w-auto flex-shrink-0 p-0 hover:bg-transparent",
            isDone ? "text-emerald-500 hover:text-slate-400" : "text-slate-300 hover:text-emerald-500"
          )}
          title={isDone ? "Reopen task" : "Mark complete"}
        >
          {isDone
            ? <CheckCircle2 className="h-5 w-5" />
            : <Circle className="h-5 w-5" />
          }
        </Button>
      </Can>

      <div className="min-w-0 flex-1">
        <div className="flex items-start justify-between gap-2">
          <p className={cn("text-sm font-medium leading-snug", isDone ? "line-through text-slate-400" : "text-slate-800")}>
            {task.title}
          </p>

          {/* Actions */}
          <div className="flex items-center gap-1 flex-shrink-0">
            {isDone && onReopen && (
              <Can resource="tasks" action="update">
                <Button variant="ghost" size="icon" onClick={() => onReopen(task.id)} className="h-6 w-6 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Reopen">
                  <RotateCcw className="h-3.5 w-3.5" />
                </Button>
              </Can>
            )}
            {!isDone && onUnsnooze && (
              <Can resource="tasks" action="update">
                <Button variant="ghost" size="icon" onClick={() => onUnsnooze(task.id)} className="h-6 w-6 rounded p-1 text-violet-500 hover:bg-violet-50 hover:text-violet-700" title="Unsnooze">
                  <BellRing className="h-3.5 w-3.5" />
                </Button>
              </Can>
            )}
            {!isDone && onSnooze && (
              <Can resource="tasks" action="update">
                <Button variant="ghost" size="icon" onClick={() => onSnooze(task.id)} className="h-6 w-6 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Snooze 1 hour">
                  <BellOff className="h-3.5 w-3.5" />
                </Button>
              </Can>
            )}
            {onEdit && (
              <Can resource="tasks" action="update">
                <Button variant="ghost" size="icon" onClick={() => onEdit(task)} className="h-6 w-6 rounded p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-700" title="Edit">
                  <Pencil className="h-3.5 w-3.5" />
                </Button>
              </Can>
            )}
            {onDelete && (
              <Can resource="tasks" action="destroy">
                <Button variant="ghost" size="icon" onClick={() => onDelete(task.id)} className="h-6 w-6 rounded p-1 text-slate-400 hover:bg-red-50 hover:text-red-500" title="Delete">
                  <Trash2 className="h-3.5 w-3.5" />
                </Button>
              </Can>
            )}
          </div>
        </div>

        {task.description && (
          <p className="mt-0.5 text-[12px] text-slate-500 line-clamp-2">{task.description}</p>
        )}

        <div className="mt-1.5 flex flex-wrap items-center gap-2.5">
          {/* Priority dot */}
          <span className="flex items-center gap-1 text-[11px] font-medium">
            <span className={cn("h-1.5 w-1.5 rounded-full", p.dot)} />
            <span className={p.text}>{p.label}</span>
          </span>

          {/* Due date */}
          {task.due_at && (
            <span className={cn("flex items-center gap-1 text-[11px]", isOverdue ? "text-red-600 font-medium" : "text-slate-400")}>
              <Clock className="h-3 w-3" />
              {formatDue(task.due_at)}
            </span>
          )}

          {/* Assignee */}
          {task.assignee && (
            <span className="flex items-center gap-1 text-[11px] text-slate-400">
              <User className="h-3 w-3" />
              {task.assignee.name}
            </span>
          )}

          {/* Context (lead/deal name) */}
          {showContext && task.taskable && (
            <span className="rounded-full bg-slate-100 px-1.5 py-0.5 text-[10px] text-slate-500">
              {task.taskable.type}
            </span>
          )}

          {/* Snoozed indicator */}
          {task.snoozed_until && new Date(task.snoozed_until) > new Date() && (
            <span className="flex items-center gap-1 text-[11px] text-violet-600">
              <AlarmClock className="h-3 w-3" /> Snoozed until {formatDue(task.snoozed_until)}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
